import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Button,
  Link,
  Chip,
  GlobalStyles,
} from "@mui/material";
import { motion } from "framer-motion";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

const COLORS = {
  orange: "#d37d36",
  orangeDark: "#b8662b",
  blue: "#1e4b81",
  blueDark: "#102840",
  white: "#ffffff",
  bg: "#f7f9fc",
  secondary: "#64748b",
  borderSoft: "rgba(16, 40, 64, 0.07)",
};

const statusConfig = {
  loading: {
    chip: "Verificando cuenta",
    title: "Confirmando tu cuenta",
    text: "Estamos validando tu correo electrónico. Este proceso solo tardará unos segundos.",
    icon: null,
    severity: "info",
  },
  success: {
    chip: "Cuenta confirmada",
    title: "Cuenta confirmada correctamente",
    text: "Tu cuenta ha sido activada con éxito. En unos segundos te llevaremos al inicio de sesión.",
    icon: <MarkEmailReadOutlinedIcon />,
    severity: "success",
  },
  alreadyConfirmed: {
    chip: "Cuenta ya activa",
    title: "Esta cuenta ya estaba confirmada",
    text: "Puedes iniciar sesión con tus credenciales y acceder a tu espacio de evaluación.",
    icon: <InfoOutlinedIcon />,
    severity: "info",
  },
  error: {
    chip: "No se pudo confirmar",
    title: "Hubo un problema al confirmar tu cuenta",
    text: "El enlace puede haber caducado o no ser válido. Inténtalo de nuevo o contacta con soporte.",
    icon: <ErrorOutlineOutlinedIcon />,
    severity: "error",
  },
};

const ConfirmPage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const confirmAccount = async () => {
      try {
        const response = await axiosInstance.get(API_ENDPOINTS.auth.confirm(email || ""));

        if (response.status === 200) {
          setStatus("success");
          setTimeout(() => navigate("/login"), 2200);
        } else if (response.status === 400) {
          setStatus("alreadyConfirmed");
        } else {
          setStatus("error");
        }
      } catch (error) {
        if (error.response?.status === 400) {
          setStatus("alreadyConfirmed");
        } else {
          setStatus("error");
        }
      }
    };

    if (email) {
      confirmAccount();
    } else {
      setStatus("error");
    }
  }, [email, navigate]);

  const current = statusConfig[status] || statusConfig.error;

  return (
    <>
      <GlobalStyles
        styles={{
          html: {
            fontSize: "14.5px",
            overflowX: "hidden",
          },
          body: {
            margin: 0,
            overflowX: "hidden",
            backgroundColor: COLORS.bg,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "*": {
            boxSizing: "border-box",
          },
          "@keyframes pulseDot": {
            "0%": { transform: "scale(1)", opacity: 1 },
            "60%": { transform: "scale(1.9)", opacity: 0 },
            "100%": { transform: "scale(1.9)", opacity: 0 },
          },
        }}
      />

      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
          bgcolor: COLORS.bg,
          background:
            "radial-gradient(circle at 14% 14%, rgba(30,75,129,0.11), transparent 30%), radial-gradient(circle at 86% 16%, rgba(211,125,54,0.13), transparent 28%), linear-gradient(180deg,#ffffff 0%,#f7f9fc 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.58,
            backgroundImage:
              "linear-gradient(rgba(16,40,64,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(16,40,64,0.035) 1px, transparent 1px)",
            backgroundSize: "52px 52px",
            pointerEvents: "none",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 430, position: "relative", zIndex: 1 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.4, sm: 3.2 },
              borderRadius: "28px",
              border: `1px solid ${COLORS.borderSoft}`,
              bgcolor: "rgba(255,255,255,0.94)",
              boxShadow:
                "0 24px 70px rgba(16,40,64,0.10), inset 0 1px 0 rgba(255,255,255,0.92)",
              backdropFilter: "blur(18px)",
              textAlign: "center",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2.3 }}>
              <Stack direction="row" spacing={1.15} alignItems="center" justifyContent="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "16px",
                    bgcolor: COLORS.white,
                    border: `1px solid ${COLORS.borderSoft}`,
                    boxShadow: "0 12px 30px rgba(16,40,64,0.08)",
                    display: "grid",
                    placeItems: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src="/images/logo_simbolo.png"
                    alt="DQPa"
                    sx={{ width: 36, height: 36, objectFit: "contain" }}
                  />
                </Box>

                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      color: COLORS.blueDark,
                      fontSize: "1.45rem",
                      fontWeight: 950,
                      letterSpacing: "-0.05em",
                      lineHeight: 1,
                    }}
                  >
                    DQPa
                  </Typography>
                  <Typography
                    sx={{
                      color: COLORS.secondary,
                      fontSize: "0.64rem",
                      fontWeight: 800,
                      letterSpacing: "0.055em",
                      textTransform: "uppercase",
                      mt: 0.4,
                    }}
                  >
                    Data Quality Process Assessment
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Chip
              icon={<VerifiedOutlinedIcon sx={{ color: `${COLORS.orange} !important`, fontSize: "17px !important" }} />}
              label={current.chip}
              size="small"
              sx={{
                height: 28,
                mb: 1.3,
                bgcolor: "rgba(211,125,54,0.09)",
                color: COLORS.blueDark,
                fontWeight: 900,
                border: "1px solid rgba(211,125,54,0.16)",
                "& .MuiChip-label": { fontSize: "0.72rem" },
              }}
            />

            <Box
              sx={{
                width: 68,
                height: 68,
                mx: "auto",
                mb: 2,
                borderRadius: "22px",
                display: "grid",
                placeItems: "center",
                bgcolor:
                  status === "error"
                    ? "rgba(211,47,47,0.08)"
                    : status === "alreadyConfirmed"
                      ? "rgba(30,75,129,0.07)"
                      : "rgba(211,125,54,0.10)",
                color:
                  status === "error"
                    ? "#b91c1c"
                    : status === "alreadyConfirmed"
                      ? COLORS.blue
                      : COLORS.orange,
                border:
                  status === "error"
                    ? "1px solid rgba(211,47,47,0.14)"
                    : "1px solid rgba(211,125,54,0.16)",
              }}
            >
              {status === "loading" ? (
                <CircularProgress size={30} sx={{ color: COLORS.orange }} />
              ) : (
                React.cloneElement(current.icon, { sx: { fontSize: 34 } })
              )}
            </Box>

            <Typography
              component="h1"
              sx={{
                color: COLORS.blueDark,
                fontWeight: 950,
                fontSize: { xs: "1.45rem", sm: "1.65rem" },
                letterSpacing: "-0.04em",
                lineHeight: 1.1,
                mb: 0.85,
              }}
            >
              {current.title}
            </Typography>

            <Typography
              sx={{
                color: COLORS.secondary,
                fontSize: "0.88rem",
                lineHeight: 1.55,
                maxWidth: 340,
                mx: "auto",
                mb: 2,
              }}
            >
              {current.text}
            </Typography>

            {email && (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: "16px",
                  bgcolor: "rgba(30,75,129,0.045)",
                  border: "1px solid rgba(30,75,129,0.07)",
                  mb: 2,
                }}
              >
                <Typography sx={{ color: COLORS.secondary, fontSize: "0.74rem", fontWeight: 750, mb: 0.2 }}>
                  Correo asociado
                </Typography>
                <Typography sx={{ color: COLORS.blueDark, fontSize: "0.84rem", fontWeight: 900, wordBreak: "break-word" }}>
                  {email}
                </Typography>
              </Box>
            )}

            {status !== "loading" && (
              <Alert
                severity={current.severity}
                sx={{
                  borderRadius: "14px",
                  fontWeight: 650,
                  fontSize: "0.84rem",
                  mb: 2,
                  textAlign: "left",
                }}
              >
                {current.text}
              </Alert>
            )}

            <Stack spacing={1.2}>
              {(status === "success" || status === "alreadyConfirmed") && (
                <Button
                  component={RouterLink}
                  to="/login"
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.25,
                    minHeight: 46,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 900,
                    fontSize: "0.92rem",
                    bgcolor: COLORS.blue,
                    boxShadow: "0 14px 30px rgba(30,75,129,0.20)",
                    "&:hover": {
                      bgcolor: COLORS.blueDark,
                      boxShadow: "0 18px 38px rgba(16,40,64,0.26)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Ir al inicio de sesión
                </Button>
              )}

              {status === "error" && (
                <Button
                  component={RouterLink}
                  to="/register"
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.25,
                    minHeight: 46,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 900,
                    fontSize: "0.92rem",
                    bgcolor: COLORS.blue,
                    boxShadow: "0 14px 30px rgba(30,75,129,0.20)",
                    "&:hover": {
                      bgcolor: COLORS.blueDark,
                      boxShadow: "0 18px 38px rgba(16,40,64,0.26)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Crear cuenta de nuevo
                </Button>
              )}

              <Typography sx={{ color: COLORS.secondary, fontSize: "0.8rem", lineHeight: 1.45 }}>
                ¿Necesitas volver?{" "}
                <Link
                  component={RouterLink}
                  to="/"
                  sx={{
                    color: COLORS.orange,
                    fontWeight: 900,
                    textDecoration: "none",
                    "&:hover": { color: COLORS.orangeDark },
                  }}
                >
                  Ir a la página principal
                </Link>
              </Typography>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 2.2 }}>
            <Box
              sx={{
                position: "relative",
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: COLORS.orange,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  bgcolor: COLORS.orange,
                  animation: "pulseDot 2s ease-out infinite",
                },
              }}
            />
            <Typography align="center" sx={{ color: COLORS.secondary, fontSize: "0.74rem", lineHeight: 1.55 }}>
              Confirmación segura para activar el acceso a DQPa.
            </Typography>
          </Stack>
        </motion.div>
      </Box>
    </>
  );
};

export default ConfirmPage;
